import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../supabaseClient'
import EmptyState from '../ui/EmptyState'
import ErrorState from '../ui/ErrorState'
import LoadingState from '../ui/LoadingState'
import SectionHeader from '../ui/SectionHeader'
import StatPill from '../ui/StatPill'
import { formatTitleText } from '../utils/displayText'
import { isSchemaMismatchError } from './schemaGuard'
import { formatDateTime } from '../utils/dateFormatting'
import { toLower } from '../utils/stringFormatting'

export default function CorrectionCenterPanel({
  onOpenIssueCenter,
  onOpenWorkers,
  onOpenJobs,
  onOpenTimeAdmin,
  onOpenAccess,
  onOpenPayStatus
}) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [optionalNotice, setOptionalNotice] = useState('')

  const [timeEntries, setTimeEntries] = useState([])
  const [requests, setRequests] = useState([])
  const [payRecords, setPayRecords] = useState([])
  const [profiles, setProfiles] = useState([])
  const [workers, setWorkers] = useState([])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    setOptionalNotice('')

    const [entriesResponse, requestsResponse, payResponse, profilesResponse, workersResponse] = await Promise.all([
      supabase
        .from('employee_time_entries')
        .select('id, worker_id, job_id, clocked_in_at, clocked_out_at, manual_adjustment, adjustment_reason, approval_status, updated_at')
        .order('updated_at', { ascending: false })
        .limit(250),
      supabase
        .from('employee_requests')
        .select('id, request_type, status, target_worker_id, current_stage, created_at, updated_at')
        .order('updated_at', { ascending: false })
        .limit(250),
      supabase
        .from('pay_status_records')
        .select('id, worker_id, job_id, payment_status, dispute_flag, last_updated_at')
        .order('last_updated_at', { ascending: false })
        .limit(250),
      supabase
        .from('profiles')
        .select('id, access_role, work_status, worker_id, display_name')
        .order('created_at', { ascending: false })
        .limit(250),
      supabase.from('workers').select('id, name, is_active, work_status, role').order('name')
    ])

    const requestsSchemaMismatch = isSchemaMismatchError(requestsResponse.error)
    const paySchemaMismatch = isSchemaMismatchError(payResponse.error)
    const profilesSchemaMismatch = isSchemaMismatchError(profilesResponse.error)

    if (
      entriesResponse.error ||
      workersResponse.error ||
      (requestsResponse.error && !requestsSchemaMismatch) ||
      (payResponse.error && !paySchemaMismatch) ||
      (profilesResponse.error && !profilesSchemaMismatch)
    ) {
      setTimeEntries([])
      setRequests([])
      setPayRecords([])
      setProfiles([])
      setWorkers([])
      setError(
        entriesResponse.error?.message ||
          requestsResponse.error?.message ||
          payResponse.error?.message ||
          profilesResponse.error?.message ||
          workersResponse.error?.message ||
          'Unable to load correction center.'
      )
      setLoading(false)
      return
    }

    const missingDatasets = []
    if (requestsSchemaMismatch) missingDatasets.push('employee requests')
    if (paySchemaMismatch) missingDatasets.push('pay status')
    if (profilesSchemaMismatch) missingDatasets.push('profile linkage')
    if (missingDatasets.length > 0) {
      setOptionalNotice(
        `Some correction datasets are unavailable in this environment: ${missingDatasets.join(', ')}. Core correction workflows remain accessible.`
      )
    }

    setTimeEntries(entriesResponse.data || [])
    setRequests(requestsSchemaMismatch ? [] : requestsResponse.data || [])
    setPayRecords(paySchemaMismatch ? [] : payResponse.data || [])
    setProfiles(profilesSchemaMismatch ? [] : profilesResponse.data || [])
    setWorkers(workersResponse.data || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const workerById = useMemo(() => new Map(workers.map((worker) => [String(worker.id), worker])), [workers])

  const staleOpenPunches = useMemo(() => {
    const staleBoundaryMs = Date.now() - 10 * 60 * 60 * 1000
    return timeEntries
      .filter((entry) => !entry.clocked_out_at)
      .filter((entry) => {
        const startedMs = new Date(entry.clocked_in_at || '').getTime()
        return Number.isFinite(startedMs) && startedMs < staleBoundaryMs
      })
  }, [timeEntries])

  const manualAdjustments = useMemo(
    () => timeEntries.filter((entry) => entry.manual_adjustment === true).slice(0, 10),
    [timeEntries]
  )

  const openCorrections = useMemo(
    () => requests.filter((request) => toLower(request.request_type) === 'time_entry_correction' && ['pending', 'escalated'].includes(toLower(request.status))),
    [requests]
  )

  const payMismatches = useMemo(
    () => payRecords.filter((record) => ['unpaid', 'partial', 'disputed'].includes(toLower(record.payment_status)) || record.dispute_flag === true),
    [payRecords]
  )

  const profileRepairs = useMemo(() => {
    return profiles.filter((profile) => {
      if (!profile.worker_id) return true
      const worker = workerById.get(String(profile.worker_id))
      if (!worker) return true
      if (worker.is_active === false || toLower(worker.work_status) !== 'active') return true
      return false
    })
  }, [profiles, workerById])

  const actionCards = useMemo(
    () => [
      {
        key: 'stale-punches',
        title: 'Fix Bad Punches',
        detail: `${staleOpenPunches.length} stale open punch${staleOpenPunches.length === 1 ? '' : 'es'} need closure or correction.`,
        cta: 'Open Time Admin',
        run: onOpenTimeAdmin
      },
      {
        key: 'assignment-corrections',
        title: 'Fix Worker-Job Assignment',
        detail: `${openCorrections.length} open correction request${openCorrections.length === 1 ? '' : 's'} may require assignment repair.`,
        cta: 'Open Jobs',
        run: onOpenJobs
      },
      {
        key: 'pay-mismatches',
        title: 'Fix Pay-Status Mismatch',
        detail: `${payMismatches.length} pay-status record${payMismatches.length === 1 ? '' : 's'} need follow-up.`,
        cta: 'Open Pay Status',
        run: onOpenPayStatus || onOpenTimeAdmin
      },
      {
        key: 'identity-repairs',
        title: 'Repair Worker/Profile Linkage',
        detail: `${profileRepairs.length} profile${profileRepairs.length === 1 ? '' : 's'} show binding or lifecycle issues.`,
        cta: 'Open Access Admin',
        run: onOpenAccess
      },
      {
        key: 'open-issues',
        title: 'Resolve Open Issues',
        detail: `${openCorrections.length} correction issue${openCorrections.length === 1 ? '' : 's'} remain unresolved.`,
        cta: 'Open Issue Center',
        run: onOpenIssueCenter
      }
    ],
    [onOpenAccess, onOpenIssueCenter, onOpenJobs, onOpenPayStatus, onOpenTimeAdmin, openCorrections.length, payMismatches.length, profileRepairs.length, staleOpenPunches.length]
  )

  if (loading) {
    return <LoadingState title="Loading correction center" message="Building repair queues from time, pay, and identity data." />
  }

  if (error) {
    return <ErrorState title="Correction center unavailable" message={error} />
  }

  return (
    <section className="gg-module-stack">
      <section className="gg-module-panel">
        <SectionHeader
          eyebrow="Admin"
          title="Correction Center"
          description="Run non-destructive repair actions for punch, assignment, pay-status, and identity linkage issues."
        />
        <div className="gg-tool-pills">
          <StatPill label="Stale Punches" value={staleOpenPunches.length} tone={staleOpenPunches.length ? 'warning' : 'info'} />
          <StatPill label="Manual Adjustments" value={manualAdjustments.length} tone={manualAdjustments.length ? 'warning' : 'info'} />
          <StatPill label="Pay Mismatches" value={payMismatches.length} tone={payMismatches.length ? 'warning' : 'info'} />
          <StatPill label="Profile Repairs" value={profileRepairs.length} tone={profileRepairs.length ? 'error' : 'info'} />
        </div>
        {optionalNotice ? <p className="gg-inline-alert gg-inline-alert--warning">{optionalNotice}</p> : null}
      </section>

      <section className="gg-module-panel">
        <p className="gg-module-panel__label">Prioritized Repair Actions</p>
        <div className="gg-action-checklist">
          {actionCards.map((card) => (
            <article key={card.key} className="gg-action-checklist__item">
              <p className="gg-record-item__title">{card.title}</p>
              <p className="gg-record-item__meta">{card.detail}</p>
              <button type="button" className="gg-button gg-button--ghost" onClick={card.run}>
                {card.cta}
              </button>
            </article>
          ))}
        </div>
      </section>

      <section className="gg-module-panel">
        <p className="gg-module-panel__label">Manual Adjustment Audit Samples</p>
        {manualAdjustments.length === 0 ? (
          <EmptyState title="No manual adjustments" message="No recent manual adjustment entries detected." />
        ) : (
          <div className="gg-record-list">
            {manualAdjustments.map((entry) => {
              const worker = workerById.get(String(entry.worker_id))
              return (
                <article key={entry.id} className="gg-record-item">
                  <p className="gg-record-item__title">{formatTitleText(worker?.name, 'Worker')} • Entry {entry.id}</p>
                  <p className="gg-record-item__meta">Reason: {entry.adjustment_reason || 'No reason'} • Status {formatTitleText(entry.approval_status, 'Pending')}</p>
                  <p className="gg-record-item__meta">Updated {formatDateTime(entry.updated_at)}</p>
                </article>
              )
            })}
          </div>
        )}
      </section>

      <section className="gg-module-panel">
        <p className="gg-module-panel__label">Identity Repair Queue</p>
        {profileRepairs.length === 0 ? (
          <EmptyState title="No profile repairs" message="No broken worker/profile linkage was detected." />
        ) : (
          <div className="gg-record-list">
            {profileRepairs.slice(0, 15).map((profile) => {
              const worker = profile.worker_id ? workerById.get(String(profile.worker_id)) : null
              return (
                <article key={profile.id} className="gg-record-item">
                  <p className="gg-record-item__title">{formatTitleText(profile.display_name, profile.id)}</p>
                  <p className="gg-record-item__meta">Access {formatTitleText(profile.access_role, 'unknown')} • Status {formatTitleText(profile.work_status, 'unknown')}</p>
                  <p className="gg-record-item__meta">Worker {worker ? formatTitleText(worker.name, worker.id) : 'Not linked / inactive'}</p>
                </article>
              )
            })}
          </div>
        )}
      </section>

      <section className="gg-module-panel">
        <p className="gg-module-panel__label">Archive / Deactivate Guidance</p>
        <p className="gg-text-soft">
          Correction actions should archive/deactivate incorrect records where possible. Avoid destructive delete paths so history remains auditable.
        </p>
        <div className="gg-form-actions">
          <button type="button" className="gg-button gg-button--ghost" onClick={onOpenWorkers}>
            Open Worker Archive Controls
          </button>
          <button type="button" className="gg-button gg-button--ghost" onClick={onOpenJobs}>
            Open Job Assignment Controls
          </button>
        </div>
      </section>
    </section>
  )
}
